import React, { useState, useEffect } from 'react';
import { Modal, Table, Empty, Tooltip } from 'antd';
import { HistoryOutlined, UserOutlined, ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';

// ─── Status colour palette ───────────────────────────────────────────────────
export const STATUS_COLOR_MAP: Record<string, { bg: string; text: string; border: string }> = {
    // ── ACCEPTED group ────────────────────────────────────────────────────────
    NEW: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-300' },
    ASSIST_CONVERT: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' },
    VERIFICATION_PENDING: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-300' },
    CUSTOMER_EXCHANGE_VERIFIED: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300' },
    VERIFIED: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300' },

    // ── AWAITING PAYMENT CONFIRMATION ─────────────────────────────────────────
    PAYMENT_PENDING: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-300' },
    PAYMENT_GATEWAY_PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-400' },

    // ── PAYMENT FAILED ────────────────────────────────────────────────────────
    PAYMENT_FAILED: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-300' },

    // ── PROCESSING group ──────────────────────────────────────────────────────
    SUPPLIER_ASSIGNED: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-300' },
    FAILED_SUPPLIER: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-400' },
    PICKUP_SCHEDULED: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-300' },
    LOGISTIC_PICKUP_ACTIVATE: { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-300' },
    OUT_FOR_PICKUP: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-300' },
    PICKUP_PENDING: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
    PICKUP_DONE: { bg: 'bg-purple-200', text: 'text-purple-800', border: 'border-purple-400' },
    INVENTORY_PICKUP: { bg: 'bg-fuchsia-50', text: 'text-fuchsia-700', border: 'border-fuchsia-300' },
    SUPPLIER_EXCHANGE_REQUESTED: { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700', border: 'border-fuchsia-300' },
    SUPPLIER_OUT_FOR_EXCHANGE: { bg: 'bg-fuchsia-200', text: 'text-fuchsia-800', border: 'border-fuchsia-400' },
    QC_FAIL: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
    QC_PASS: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
    DROPSHIP_DETECTED: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
    DROPSHIP_ACTIVATED: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
    SHIPMENT_CREATED: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-300' },

    // ── PACKED ────────────────────────────────────────────────────────────────
    PACKED: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-300' },
    MANIFEST_CREATED: { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-300' },

    // ── SHIPPED ───────────────────────────────────────────────────────────────
    SHIPPED: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-300' },

    // ── DELIVERED ─────────────────────────────────────────────────────────────
    DELIVERED: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-300' },
    CLOSED: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-400' },

    // ── RETURN REJECTED ───────────────────────────────────────────────────────
    RETURN_NOT_REQUIRED: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-300' },
    REQUEST_REJECTED: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-400' },

    // ── EXCHANGE flows ────────────────────────────────────────────────────────
    CUSTOMER_EXCHANGE_REQUESTED: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300' },
    EXCHANGE_APPROVED: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
    CUSTOMER_EXCHANGE_APPROVED: { bg: 'bg-amber-200', text: 'text-amber-800', border: 'border-amber-400' },

    // ── RETURN flows ──────────────────────────────────────────────────────────
    RETURN_REQUESTED: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-300' },
    RETURN_APPROVED: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300' },
    RVP_RAISED: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-300' },
    RETURN_PICKED: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-300' },
    WAREHOUSE_RETURN: { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-300' },
    RTV_INITIATED: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300' },
    RTV_DONE: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-400' },
    RTV_DENIED: { bg: 'bg-red-200', text: 'text-red-800', border: 'border-red-400' },
    RETURN_QCPASS: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-300' },
    RETURN_QCFAIL: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300' },
    TAX_PO: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' },
    INVENTORIZE: { bg: 'bg-gray-200', text: 'text-gray-700', border: 'border-gray-400' },

    // ── CANCELLED group ───────────────────────────────────────────────────────
    RETURN_TO_ORIGIN: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-300' },
    CUSTOMER_CANCEL: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-400' },
    ADMIN_CANCEL: { bg: 'bg-red-200', text: 'text-red-800', border: 'border-red-500' },
    DUMMY_ORDER: { bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-300' },

    // ── COMMENT (internal note) ───────────────────────────────────────────────
    COMMENT: { bg: 'bg-yellow-50', text: 'text-yellow-800', border: 'border-yellow-300' },

    // ── Manual Transition (catch-all) ─────────────────────────────────────────
    MANUAL_TRANSITION: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' },
};

const DEFAULT_STATUS_STYLE = { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' };

// ─── Delta formatter ─────────────────────────────────────────────────────────
const formatDelta = (mins: number): string => {
    if (mins < 1) return '< 1m';
    const days = Math.floor(mins / (60 * 24));
    const hours = Math.floor((mins % (60 * 24)) / 60);
    const m = Math.floor(mins % 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${m}m`;
    return `${m}m`;
};

// ─── Table columns ────────────────────────────────────────────────────────────
const ITEM_HISTORY_COLUMNS = [
    {
        title: 'Date & Time',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 110,
        render: (val: string) =>
            val ? (
                <span className="text-[11px] text-gray-500 whitespace-nowrap">
                    {moment(val, 'YYYY-MM-DD HH:mm:ss').format('DD MMM YY')}
                    <br />
                    <span className="text-gray-400">{moment(val, 'YYYY-MM-DD HH:mm:ss').format('hh:mm A')}</span>
                </span>
            ) : '—',
    },
    {
        title: '⏱ Time to Reach',
        dataIndex: 'timeDelta',
        key: 'timeDelta',
        width: 115,
        render: (val: number | null) => {
            if (val === null || val === undefined)
                return <span className="text-[10px] text-gray-300">—</span>;
            const label = formatDelta(val);
            const colorClass = val < 60
                ? 'text-emerald-700 bg-emerald-50 border-emerald-300'
                : val < 240
                    ? 'text-blue-700 bg-blue-50 border-blue-300'
                    : val < 1440
                        ? 'text-orange-700 bg-orange-50 border-orange-300'
                        : 'text-red-700 bg-red-50 border-red-300';
            return (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap ${colorClass}`}>
                    ⏱ {label}
                </span>
            );
        },
    },
    {
        title: 'Status',
        dataIndex: 'itemStatusName',
        key: 'itemStatusName',
        width: 130,
        render: (val: string) => {
            const s = STATUS_COLOR_MAP[val] || DEFAULT_STATUS_STYLE;
            return (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border whitespace-nowrap ${s.text} ${s.bg} ${s.border}`}>
                    {val}
                </span>
            );
        },
    },
    {
        title: 'Action / Remark',
        key: 'details',
        width: 400,
        render: (_: any, row: any) => (
            <div>
                {row.action && (
                    <p className="text-[11px] font-semibold text-gray-700 leading-snug mb-0.5">{row.action}</p>
                )}
                {row.remark && (
                    <p className="text-[11px] text-gray-500 leading-snug">{row.remark.trim()}</p>
                )}
            </div>
        ),
    },
    {
        title: 'Actor',
        dataIndex: 'userEmail',
        key: 'userEmail',
        width: 180,
        ellipsis: true,
        render: (val: string) =>
            val ? (
                <span className="flex items-center gap-1 text-[11px] text-gray-500">
                    <UserOutlined className="text-gray-300 flex-shrink-0" />
                    <span className="truncate">{val}</span>
                </span>
            ) : null,
    },
    {
        title: 'Supplier / Price',
        key: 'extra',
        width: 120,
        render: (_: any, row: any) => (
            <div className="flex flex-col gap-1">
                {row.supplierName && (
                    <span className="text-[10px] text-indigo-600 font-medium bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded leading-none">
                        {row.supplierName}
                    </span>
                )}
                {row.transferPrice != null && (
                    <span className="text-[11px] text-emerald-600 font-semibold">₹{row.transferPrice}</span>
                )}
            </div>
        ),
    },
];

// ─── Main modal component ─────────────────────────────────────────────────────
interface ItemHistoryModalProps {
    /** Item ID to fetch history for. Pass null/undefined to close the modal. */
    itemId: string | null | undefined;
    onClose: () => void;
}

const ItemHistoryModal: React.FC<ItemHistoryModalProps> = ({ itemId, onClose }) => {
    const [history, setHistory] = useState<any[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [totalJourneyMins, setTotalJourneyMins] = useState<number | null>(null);
    const [journeyStart, setJourneyStart] = useState<string | null>(null);
    const [journeyEnd, setJourneyEnd] = useState<string | null>(null);

    useEffect(() => {
        if (!itemId) return;
        setLoadingHistory(true);
        setHistoryError('');
        setHistory([]);
        axiosInstance
            .get(`${apis.FETCH_ORDER_ITEM_HISTORY}?itemId=${itemId}`)
            .then(res => {
                const raw: any[] = res.data?.data || [];
                // Compute total journey span
                if (raw.length >= 2) {
                    const first = moment(raw[0].createdAt, 'YYYY-MM-DD HH:mm:ss');
                    const last = moment(raw[raw.length - 1].createdAt, 'YYYY-MM-DD HH:mm:ss');
                    setTotalJourneyMins(Math.max(0, last.diff(first, 'minutes')));
                    setJourneyStart(raw[0].createdAt);
                    setJourneyEnd(raw[raw.length - 1].createdAt);
                } else {
                    setTotalJourneyMins(null);
                    setJourneyStart(raw[0]?.createdAt ?? null);
                    setJourneyEnd(null);
                }
                // timeDelta[i] = minutes from raw[i-1] → raw[i] (time to REACH this stage)
                const withDelta = raw.map((entry, i) => {
                    if (i === 0) return { ...entry, timeDelta: null };
                    const prev = moment(raw[i - 1].createdAt, 'YYYY-MM-DD HH:mm:ss');
                    const curr = moment(entry.createdAt, 'YYYY-MM-DD HH:mm:ss');
                    const diffMins = Math.max(0, curr.diff(prev, 'minutes'));
                    return { ...entry, timeDelta: diffMins };
                });
                setHistory(withDelta); // oldest first, latest at bottom
            })
            .catch(() => setHistoryError('Failed to load item history.'))
            .finally(() => setLoadingHistory(false));
    }, [itemId]);

    // Journey duration colour: same scale as per-stage delta badges
    const journeyColor = totalJourneyMins === null ? ''
        : totalJourneyMins < 60 ? 'text-emerald-700 bg-emerald-50 border-emerald-300'
            : totalJourneyMins < 1440 ? 'text-blue-700   bg-blue-50   border-blue-300'
                : totalJourneyMins < 10080 ? 'text-orange-700 bg-orange-50 border-orange-300'
                    : 'text-red-700   bg-red-50   border-red-300';

    return (
        <Modal
            open={!!itemId}
            onCancel={onClose}
            footer={null}
            title={
                <div className="flex items-center gap-2 flex-wrap">
                    <HistoryOutlined className="text-indigo-500" />
                    <span className="font-bold text-gray-800">Item History</span>
                    {itemId && (
                        <span className="font-mono text-sm text-indigo-600 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded">
                            #{itemId}
                        </span>
                    )}
                    {/* Journey summary strip — visible once data loads */}
                    {!loadingHistory && history.length > 0 && (
                        <div className="ml-auto flex items-center gap-3 flex-wrap">
                            {/* Created → Last stage */}
                            {journeyStart && (
                                <span className="flex items-center gap-1 text-[11px] text-gray-500">
                                    <ClockCircleOutlined className="text-gray-300" />
                                    <span className="font-medium">
                                        {moment(journeyStart, 'YYYY-MM-DD HH:mm:ss').format('DD MMM YY')}
                                    </span>
                                    {journeyEnd && (
                                        <>
                                            <span className="text-gray-300 mx-0.5">→</span>
                                            <span className="font-medium">
                                                {moment(journeyEnd, 'YYYY-MM-DD HH:mm:ss').format('DD MMM YY')}
                                            </span>
                                        </>
                                    )}
                                </span>
                            )}
                            {/* Total journey duration pill */}
                            {totalJourneyMins !== null && (
                                <Tooltip title="Total time from first event to last event">
                                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${journeyColor}`}>
                                        ⏱ Total: {formatDelta(totalJourneyMins)}
                                    </span>
                                </Tooltip>
                            )}
                            <span className="text-[11px] text-gray-400">{history.length} events</span>
                        </div>
                    )}
                </div>
            }
            width={1300}
            style={{ top: 40 }}
            styles={{ body: { padding: '8px 0 4px 0', maxHeight: '60vh', overflowY: 'auto' } }}
        >
            {historyError && (
                <div className="text-red-500 text-center py-8">{historyError}</div>
            )}
            <Table
                columns={ITEM_HISTORY_COLUMNS}
                dataSource={history}
                rowKey={(_, idx) => String(idx)}
                loading={loadingHistory}
                pagination={false}
                size="small"
                tableLayout="fixed"
                locale={{ emptyText: <Empty description="No history found" /> }}
            />
        </Modal>
    );
};

// ─── Reusable clickable trigger badge ────────────────────────────────────────
interface ItemHistoryBadgeProps {
    itemId: string;
    onClick: (itemId: string) => void;
}


export const ItemHistoryBadge: React.FC<ItemHistoryBadgeProps> = ({ itemId, onClick }) => (
    <Tooltip title="Click to view item status history" placement="top">
        <button
            onClick={(e) => { e.stopPropagation(); onClick(itemId); }}
            className={[
                'group inline-flex items-center gap-1.5',
                'px-2 py-1 rounded-md',
                'bg-indigo-50 border border-indigo-300',
                'text-[11px] font-mono font-semibold text-indigo-600',
                'shadow-sm hover:shadow-md',
                'hover:bg-indigo-600 hover:text-white hover:border-indigo-600',
                'active:scale-95',
                'transition-all duration-150 cursor-pointer',
                'underline underline-offset-2 decoration-indigo-300 hover:no-underline',
            ].join(' ')}
        >
            <HistoryOutlined className="text-[10px] transition-transform duration-150 group-hover:rotate-12" />
            Item #{itemId}
        </button>
    </Tooltip>
);

export default ItemHistoryModal;
